import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Enhanced SEO component with multilingual support for German, Dutch, English, and French
 * Optimized for dice games, family games, and board game searches
 */
const SEO = ({ title, description, keywords, image, url, siteName, locale, language }) => {
  const siteUrl = 'https://qwixx-online.nl';

  // Multilingual SEO data by language
  const seoData = {
    en: {
      title: 'Qwixx Online - Play Free Multiplayer Dice Game with Friends',
      description: 'Play Qwixx dice game online for free with friends! Fast-paced multiplayer board game for 2-4 players. No download needed - play directly in your browser. Perfect family game night entertainment!',
      keywords: 'qwixx, qwixx online, dice game, online dice game, free dice game, multiplayer dice game, board game online, family games, play with friends, browser game, no download game, online board game, dice rolling game, quick dice game, family board game, party game, game night, steffen benndorf game, roll and write game, score pad game, qwixx app, qwixx free, play qwixx online, würfelspiel online',
      siteName: 'Qwixx Online - Free Multiplayer Dice Game',
      ogTitle: 'Qwixx Online - Play the Popular Dice Game Free with Friends',
      ogDescription: 'Fast-paced multiplayer dice game for 2-4 players. Play Qwixx online free - no download needed!'
    },
    nl: {
      title: 'Qwixx Online - Gratis Multiplayer Dobbelspel Spelen met Vrienden',
      description: 'Speel Qwixx dobbelspel gratis online met vrienden! Snel multiplayer bordspel voor 2-4 spelers. Geen download nodig - speel direct in je browser. Perfect voor gezellige spelletjesavonden!',
      keywords: 'qwixx, qwixx online, dobbelspel, online dobbelspel, gratis dobbelspel, multiplayer dobbelspel, bordspel online, familie spellen, spelen met vrienden, browser spel, online bordspel, dobbelstenen spel, snel dobbelspel, familie bordspel, gezelschapsspel, spelletjesavond, steffen benndorf spel, rol en schrijf spel, scoreblok spel, qwixx app, qwixx gratis, qwixx spelen online, würfelspiel',
      siteName: 'Qwixx Online - Gratis Multiplayer Dobbelspel',
      ogTitle: 'Qwixx Online - Speel het Populaire Dobbelspel Gratis met Vrienden',
      ogDescription: 'Snel multiplayer dobbelspel voor 2-4 spelers. Speel Qwixx gratis online - geen download nodig!'
    },
    de: {
      title: 'Qwixx Online - Kostenloses Multiplayer Würfelspiel mit Freunden Spielen',
      description: 'Spiele Qwixx Würfelspiel kostenlos online mit Freunden! Schnelles Multiplayer-Brettspiel für 2-4 Spieler. Kein Download nötig - direkt im Browser spielen. Perfekt für gemütliche Spieleabende!',
      keywords: 'qwixx, qwixx online, würfelspiel, online würfelspiel, kostenloses würfelspiel, multiplayer würfelspiel, brettspiel online, familienspiele, mit freunden spielen, browserspiel, online brettspiel, würfel spiel, schnelles würfelspiel, familien brettspiel, gesellschaftsspiel, spieleabend, steffen benndorf spiel, roll and write spiel, scoreblock spiel, qwixx app, qwixx kostenlos, qwixx online spielen, würfelspiel online kostenlos',
      siteName: 'Qwixx Online - Kostenloses Multiplayer Würfelspiel',
      ogTitle: 'Qwixx Online - Spiele das Beliebte Würfelspiel Kostenlos mit Freunden',
      ogDescription: 'Schnelles Multiplayer-Würfelspiel für 2-4 Spieler. Spiele Qwixx kostenlos online - kein Download nötig!'
    },
    fr: {
      title: 'Qwixx Online - Jeu de Dés Multijoueur Gratuit avec des Amis',
      description: 'Jouez à Qwixx en ligne gratuitement avec des amis! Jeu de société multijoueur rapide pour 2-4 joueurs. Aucun téléchargement requis - jouez directement dans votre navigateur. Parfait pour les soirées jeux en famille!',
      keywords: 'qwixx, qwixx online, jeu de dés, jeu de dés en ligne, jeu de dés gratuit, jeu de dés multijoueur, jeu de société en ligne, jeux familiaux, jouer avec des amis, jeu navigateur, jeu de société en ligne, jeu de dés rapide, jeu de société familial, jeu de société, soirée jeux, jeu steffen benndorf, roll and write, bloc de score, qwixx app, qwixx gratuit, jouer qwixx en ligne, würfelspiel',
      siteName: 'Qwixx Online - Jeu de Dés Multijoueur Gratuit',
      ogTitle: 'Qwixx Online - Jouez au Jeu de Dés Populaire Gratuitement avec des Amis',
      ogDescription: 'Jeu de dés multijoueur rapide pour 2-4 joueurs. Jouez à Qwixx gratuitement en ligne - aucun téléchargement requis!'
    }
  };

  // Get the appropriate language data, default to English
  const currentLang = language || locale?.substring(0, 2) || 'en';
  const langData = seoData[currentLang] || seoData.en;

  // Build the SEO object
  const seo = {
    title: title || langData.title,
    description: description || langData.description,
    keywords: keywords || langData.keywords,
    image: image ? `${siteUrl}${image}` : `${siteUrl}/qwixx-social-preview.png`,
    url: url || siteUrl,
    siteName: siteName || langData.siteName,
    locale: locale || `${currentLang}_${currentLang.toUpperCase()}`,
    ogTitle: langData.ogTitle,
    ogDescription: langData.ogDescription
  };

  // Structured data for search engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "VideoGame",
    "name": seo.title,
    "url": seo.url,
    "image": seo.image,
    "description": seo.description,
    "applicationCategory": "Game",
    "gameItem": "Dice Game",
    "numberOfPlayers": {
      "@type": "QuantitativeValue",
      "minValue": 2,
      "maxValue": 4
    },
    "publisher": {
      "@type": "Organization",
      "name": seo.siteName,
      "url": siteUrl
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    },
    "inLanguage": [currentLang, "en", "nl", "de", "fr"],
    "genre": ["Dice Game", "Board Game", "Family Game", "Party Game"],
    "platform": "Web Browser",
    "playMode": ["MultiPlayer", "CoOp"],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "ratingCount": "1250",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  // Alternate language URLs for international SEO
  const alternateLanguages = [
    { lang: 'en', url: `${siteUrl}?lang=en` },
    { lang: 'nl', url: `${siteUrl}?lang=nl` },
    { lang: 'de', url: `${siteUrl}?lang=de` },
    { lang: 'fr', url: `${siteUrl}?lang=fr` },
    { lang: 'x-default', url: siteUrl }
  ];

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />

      <link rel="canonical" href={seo.url} />

      {/* Alternate language links for international SEO */}
      {alternateLanguages.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seo.url} />
      <meta property="og:title" content={seo.ogTitle} />
      <meta property="og:description" content={seo.ogDescription} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.siteName} />
      <meta property="og:site_name" content={seo.siteName} />
      <meta property="og:locale" content={seo.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:title" content={seo.ogTitle} />
      <meta name="twitter:description" content={seo.ogDescription} />
      <meta name="twitter:image" content={seo.image} />

      {/* Additional SEO enhancements */}
      <meta name="author" content="Qwixx Online" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      {/* Mobile optimization */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Qwixx" />

      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
